import { ROUTES } from 'src/router/const'
import { getGrantKeysFromPayload, hasValidGrants } from 'src/services/helpers/session.helper'

/**
 * Vue Router query values can be `string | string[] | undefined`.
 * Returns the first string segment trimmed, or `null` when missing / not a string / empty after trim.
 *
 * @param {string | string[] | import('vue-router').LocationQueryValue | undefined} raw
 * @returns {string | null}
 */
export function firstTrimmedQueryString(raw) {
  if (raw === null) return null
  const first = Array.isArray(raw) ? raw[0] : raw
  if (typeof first !== 'string') return null
  const trimmed = first.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Active grant for the map: explicit `grantId` query wins; otherwise exactly one stored grant.
 *
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route
 * @param {Record<string, unknown> | null | undefined} grants
 * @returns {string | null}
 */
export function resolveActiveGrantId(route, grants) {
  const fromQuery = firstTrimmedQueryString(route.query.grantId)
  if (fromQuery) return fromQuery
  const keys = getGrantKeysFromPayload(grants)
  if (keys.length === 1) return keys[0]
  return null
}

/**
 * Pure routing decision for grant-protected routes.
 * Returns a route location to redirect to, or null to stay put.
 *
 * @param {string | undefined} routeName - current route name
 * @param {Record<string, unknown> | null} grants
 * @param {string | null} queryGrantId - `grantId` from the route query only (not inferred from grants)
 * @returns {import('vue-router').RouteLocationRaw | null}
 */
export function resolveGrantRoute(routeName, grants, queryGrantId) {
  if (routeName !== ROUTES.GRANT_SELECT && routeName !== ROUTES.MAP) return null
  if (!grants) return null
  if (!hasValidGrants(grants)) return { name: ROUTES.LOGIN }

  const keys = getGrantKeysFromPayload(grants)
  if (routeName === ROUTES.MAP) {
    if (!queryGrantId) {
      if (keys.length === 1) {
        return { name: ROUTES.MAP, query: { grantId: keys[0] } }
      }
      return { name: ROUTES.GRANT_SELECT }
    }
    if (!keys.includes(queryGrantId)) return { name: ROUTES.GRANT_SELECT }
  }

  return null
}
